'use client';

import { useMediaQuery } from '@ag1/ui';
import { useScrollPaddingTopHandler } from 'atoms/scrollPaddingTopAtom';
import clsx from 'clsx';
import Link from 'next/link';
import { usePageContext } from 'processes/Page/PageContext';
import React, { useMemo, useRef } from 'react';
import { Banner, BannerType } from 'shared/components/Header/Banner/Banner';
import { type getActiveBannerForHeader } from 'shared/utils/bannerVisibility';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { urlFor } from 'shared/utils/urlFor';
import { HeaderLanderV3, SanityKeyed } from 'types/studio/studio';
import { CommonSectionProps } from '../types';
import { UnifiedButtonType } from '../UnifiedButton';
import UnifiedButton from '../UnifiedButton/UnifiedButton';
import styles from './HeaderLanderV3.module.scss';

type HeaderLanderV3Props = SanityKeyed<HeaderLanderV3> &
  CommonSectionProps & {
    activeBanner: ReturnType<typeof getActiveBannerForHeader>;
  };

export const HeaderLanderV3Markup: React.FC<HeaderLanderV3Props> = ({
  _type: sectionType,
  bgColor,
  hideOnMobile,
  headline,
  headlineMobile,
  image,
  unifiedButton,
  primaryButtonUnderLarge,
  oldThemeButton,
  theme,
  stackable,
  activeBanner
}) => {
  const atSmall = useMediaQuery('s');
  const { sectionsUsed } = usePageContext();
  const desktopHeaderRef = useRef<HTMLDivElement | null>(null);
  const mobileHeaderRef = useRef<HTMLDivElement | null>(null);

  const { shouldDisableUnifiedButton } = usePageContext();

  const disableUnifiedButton = useMemo(
    () => shouldDisableUnifiedButton(unifiedButton),
    [shouldDisableUnifiedButton, unifiedButton]
  );

  const { height: headerHeight, top: headerTop } = useScrollPaddingTopHandler(
    {
      type: 'header',
      disable: (!atSmall && hideOnMobile) || !!activeBanner
    },
    mobileHeaderRef,
    desktopHeaderRef
  );

  const asStackableHeaderGroupItem = sectionsUsed?.navigation || false;
  const headerStyles = useMemo<React.CSSProperties>(() => {
    if (asStackableHeaderGroupItem) {
      return { position: `relative`, top: 0 };
    }
    return { top: headerTop };
  }, [asStackableHeaderGroupItem, headerTop]);

  return (
    <>
      {activeBanner && !stackable && (
        <Banner
          banner={{
            _type: 'banner',
            headline: activeBanner.headline,
            notice: activeBanner.notice,
            noticeColor: activeBanner.noticeColor,
            image: activeBanner.startIcon,
            body: activeBanner.body,
            countries: activeBanner.countries || [],
            buttonText: activeBanner.buttonText
          }}
          bannerType={BannerType.AG1_HAT_D3}
          link={activeBanner.ctaUrl}
          className={styles.banner}
        />
      )}
      <div
        className={clsx(
          styles.root,
          hideOnMobile && styles.noPadding,
          getThemeCSSClass(theme),
          activeBanner && styles.activeBanner,
          stackable && styles.stackableRoot,
          asStackableHeaderGroupItem && styles.headerGroupItem
        )}
        style={
          headerHeight
            ? {
                paddingTop: `${headerHeight}px`
              }
            : {}
        }>
        <div
          ref={mobileHeaderRef}
          className={clsx(
            styles.mobile,
            bgColor === 'white' && styles.bgWhite,
            hideOnMobile && styles.hide,
            primaryButtonUnderLarge && styles.primaryMobileButtonContainer,
            getThemeCSSClass(theme),
            activeBanner && styles.activeBanner,
            stackable && styles.stackable
          )}
          style={headerStyles}>
          <div className={clsx(primaryButtonUnderLarge && styles.primaryMobileButtonInner)}>
            {primaryButtonUnderLarge && image && !stackable && !asStackableHeaderGroupItem && (
              <Link href="/">
                <img src={urlFor(image).url} alt={image.alt} className={styles.logo} />
              </Link>
            )}
            <p className={styles.headlineMobile}>{headlineMobile}</p>
            <p className={styles.headlineTablet}>{headline}</p>
            <div
              className={clsx(
                styles.divider,
                (primaryButtonUnderLarge || stackable) && styles.displayNone
              )}>
              |
            </div>
            <UnifiedButton
              unifiedButton={unifiedButton as UnifiedButtonType}
              isDisabled={disableUnifiedButton}
              sectionType={sectionType}
              size="small"
              className={clsx(
                styles.buttonMobile,
                stackable && !primaryButtonUnderLarge && styles.displayNone,
                primaryButtonUnderLarge && styles.primaryMobileButton,
                primaryButtonUnderLarge && unifiedButton?.hoverColor === 'dark' && styles.dark,
                !primaryButtonUnderLarge && unifiedButton?.hoverColor === 'dark' && styles.darkLink
              )}
              theme={oldThemeButton ? 'old' : theme}
            />
          </div>
        </div>
        <div
          ref={desktopHeaderRef}
          className={clsx(
            styles.desktop,
            bgColor === 'white' && styles.bgWhite,
            getThemeCSSClass(theme),
            stackable && styles.stackable
          )}
          style={headerStyles}>
          {image && !stackable && !asStackableHeaderGroupItem && (
            <Link href="/">
              <img src={urlFor(image).url} alt={image.alt} className={styles.logo} />
            </Link>
          )}
          <p className={styles.headline}>{headline}</p>
          <UnifiedButton
            unifiedButton={unifiedButton as UnifiedButtonType}
            isDisabled={disableUnifiedButton}
            sectionType={sectionType}
            size="small"
            className={styles.button}
            theme={oldThemeButton ? 'old' : theme}
          />
        </div>
      </div>
    </>
  );
};
export default HeaderLanderV3Markup;
